/**
 * material.ts — the audio and the questions a PDF page can carry.
 *
 * A published document is one file and one embed, and everything a reader does
 * with it is page-located: *this* page has a clip to listen to, *these* pages ask
 * something. So material is keyed by page number, and nothing about it is
 * positional — page 7's questions stay page 7's questions when the author
 * re-compresses the file, reorders nothing and republishes.
 *
 * Two shapes travel, and the split is the point of the feature.
 *
 *  - **The record** (`#/lib/document/materialPublish`) holds the clips' addresses
 *    and the questions themselves. It is its own `app.bsky.feed.documentMaterial`
 *    record, referenced from the post, and a reader fetches it *only when they
 *    play* — so a deck in a feed costs one blob fetch and nothing more until
 *    somebody actually answers something.
 *  - **The denormalized page table** (`materialPages`) rides on the post's own
 *    embed, as `[{page, audio, questions}]`. It is what lets every book card
 *    state its badges — per page and in total — without fetching the material,
 *    which is the whole reason it exists rather than being derived on the client.
 *    Totals are *summed* from it rather than stored beside it: one fact, stated
 *    once, so the per-page badges and the deck's numbers can never disagree.
 *
 * Everything here is pure. That is what makes the caps testable: a page with six
 * questions trims to five, a question with no prompt is dropped, and duplicate or
 * out-of-range page numbers collapse — all of it without a renderer, a network or
 * a store.
 *
 * The questions are capsule `Slide`s, unmodified. The studio already knows how to
 * write them, the play engine already knows how to render them and score them, and
 * a page's material is therefore the same object a capsule holds — which is why
 * this file normalizes and counts them but never describes them in a second
 * vocabulary.
 */
import {createSlide} from '#/lib/slideFactory'
import {type Slide, type SlideOption} from '#/types/capsule'
import {
  DOCUMENT_PAGE_MAX_AUDIO,
  DOCUMENT_PAGE_MAX_QUESTIONS,
  MATERIAL_MAX_JSON_BYTES,
  MATERIAL_QUESTION_MAX_EXPLANATION_LENGTH,
  MATERIAL_QUESTION_MAX_OPTION_LENGTH,
  MATERIAL_QUESTION_MAX_OPTIONS,
  MATERIAL_QUESTION_MAX_PROMPT_LENGTH,
} from './limits'
import {type DocumentBlobRef} from './types'
import {buildPdsBlobUrl} from './util'

/**
 * The companion record's own collection.
 *
 * Deliberately *not* a lexicon entry in the PDS yet: the write that creates it
 * omits `validate`, which is how this deployment stores a record for a
 * collection it has no schema for (see `#/lib/document/materialPublish`). That
 * keeps the whole feature shippable from the client, and the collection's name
 * is declared here once so the day it *does* get a lexicon it is written in one
 * place.
 */
export const DOCUMENT_MATERIAL_RECORD_TYPE = 'app.bsky.feed.documentMaterial'

/** The two keys material adds to the `app.bsky.embed.document` record. */
export const MATERIAL_EMBED_URI_KEY = 'materialUri'
export const MATERIAL_EMBED_PAGES_KEY = 'materialPages'

/**
 * The question types a page may carry: the three a reader answers in one tap.
 *
 * The studio can write fifteen block types, and the other twelve are not refused
 * here out of snobbery — a matching grid or a fill-in-the-blanks paragraph is
 * *authored* over a page, and neither reads as a question laid over a page you
 * are still looking at. These three are the ones whose whole meaning is a single
 * prompt and some alternatives.
 */
export const MATERIAL_QUESTION_TYPES = [
  'single_choice',
  'multiple_choice',
  'true_false',
] as const

export type MaterialQuestionType = (typeof MATERIAL_QUESTION_TYPES)[number]

/**
 * One page's material, as the record holds it. `page` is 1-based.
 *
 * A page's clip travels as *two* things, and the pair is not redundancy:
 *
 *  - `audio` is the blob the clip's bytes are, and it is what makes the clip
 *    exist at all. A PDS keeps an uploaded blob only when a record references it
 *    — uploads are parked until a record's own blob reference promotes them, and
 *    a URL inside a record is not a reference. A clip named only by address is a
 *    clip the server is free to forget, and on a strict build it will refuse to
 *    serve it from the moment it is written (this one does: `Blob not found`).
 *  - `audioUrl` is the address a reader fetches, because every clip in this app —
 *    a capsule's, a book's, this page's — is spoken about by URL: the editor that
 *    records it, the field that uploads it and the player that hears it all
 *    exchange URLs, and the read path here is the same one they use.
 *
 * The address is derived from the reference whenever it is missing, so a record
 * that carries only the blob is still playable; the reference cannot be derived
 * from the address, which is why the upload is where both are set.
 */
export type DocumentPageMaterial = {
  page: number
  /** The page's one clip, as a public URL. Local takes are flushed at publish. */
  audioUrl?: string
  /**
   * The page's one clip, as the bytes it is. Present once the clip has been
   * uploaded — by the authoring field, or by the publish flush.
   */
  audio?: DocumentBlobRef
  questions: Slide[]
}

/** A document's material, as the record holds it: one row per page that has any. */
export type DocumentMaterial = {
  pages: DocumentPageMaterial[]
}

/** What a badge shows for one page. */
export type MaterialPageCounts = {
  /** Clips on the page: 0 or 1 (see {@link DOCUMENT_PAGE_MAX_AUDIO}). */
  audio: number
  questions: number
}

/** What a badge shows for the deck. */
export type MaterialTotals = MaterialPageCounts & {
  /** Pages that carry anything, so a total can say "of 28 pages, 3". */
  pagesWithMaterial: number
}

/**
 * The per-page table denormalized onto the embed, with where the material lives.
 *
 * This is the whole reader-visible summary of a document's material: a card can
 * draw every badge from it, and only a play needs the record it points at.
 */
export type DocumentMaterialRef = {
  uri: string
  pages: {page: number; audio: number; questions: number}[]
}

/**
 * The record's own shape, as written to the PDS.
 *
 * `document` names the post the material belongs to by URI alone, with no CID.
 * A strong reference exists to pin a *version*, and there is no other version:
 * the post and its material are written in one commit (see
 * `#/lib/document/materialPublish`), so a CID here would pin a record that was
 * never published on its own, and nothing reads it back that way.
 */
export type DocumentMaterialRecord = {
  $type: typeof DOCUMENT_MATERIAL_RECORD_TYPE
  document: {uri: string}
  pages: DocumentPageMaterial[]
  createdAt: string
}

export function isMaterialQuestionType(
  type: unknown,
): type is MaterialQuestionType {
  return (
    typeof type === 'string' &&
    (MATERIAL_QUESTION_TYPES as readonly string[]).includes(type)
  )
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/** Trims a string to a cap, returning `undefined` when nothing is left. */
function text(value: unknown, maxLength: number): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed ? trimmed.slice(0, maxLength) : undefined
}

/**
 * A positive 1-based page number, or `undefined`.
 *
 * Floats are refused rather than rounded: a page number is a page number, and
 * rounding `2.5` to `3` would silently move an author's questions onto the page
 * after the one they chose.
 */
function pageNumber(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined
  if (!Number.isInteger(value) || value < 1) return undefined
  return value
}

/**
 * The options worth keeping: labelled ones, in order, capped.
 *
 * An option with no words is not an option — it would render as an empty row the
 * learner can pick — so it is dropped rather than kept as a blank answer. The
 * list is capped because the cap is what bounds the record (see
 * `#/lib/document/limits`), and the *first* ones are kept because that is the
 * order the author wrote them in.
 */
function usableOptions(value: unknown, keep: number): SlideOption[] {
  if (!Array.isArray(value)) return []
  const options: SlideOption[] = []
  for (const raw of value) {
    if (!isObject(raw)) continue
    const label = text(raw.label, MATERIAL_QUESTION_MAX_OPTION_LENGTH)
    if (!label) continue
    options.push({
      id: text(raw.id, 40) ?? `o_${options.length}`,
      label,
      ...(typeof raw.imageUrl === 'string' && raw.imageUrl
        ? {imageUrl: raw.imageUrl}
        : {}),
      isCorrect: raw.isCorrect === true,
    })
    if (options.length >= keep) break
  }
  return options
}

/**
 * One question, normalised — or `undefined` when it is not a question.
 *
 * The refusals are the interesting part, and each one is a thing that would
 * otherwise reach a learner:
 *
 *  - **No id.** The play record is per *question*, so a question without an id
 *    cannot be scored or reported on. Nothing downstream could repair that, so it
 *    is dropped here rather than at the end.
 *  - **No prompt.** A row with answers and no question is not an exercise; it is
 *    a list of guesses.
 *  - **No correct answer.** A question nobody can get right is worse than no
 *    question: it teaches the learner that the book is broken.
 *  - **A single choice with two correct answers.** Contradictory as written, and
 *    the renderer would score the learner wrong for choosing the one they were
 *    shown second. The first correct answer is kept, because that is the one the
 *    author marked first, and the rest are cleared rather than the question being
 *    thrown away.
 *  - **A true/false with no verdict.** There is no default to invent.
 */
export function normalizeQuestion(value: unknown): Slide | undefined {
  if (!isObject(value)) return undefined
  const type = value.type
  if (!isMaterialQuestionType(type)) return undefined

  const id = text(value.id, 64)
  if (!id) return undefined

  const question = text(value.question, MATERIAL_QUESTION_MAX_PROMPT_LENGTH)
  if (!question) return undefined

  const explanation = text(
    value.explanation,
    MATERIAL_QUESTION_MAX_EXPLANATION_LENGTH,
  )
  const points =
    typeof value.points === 'number' &&
    Number.isFinite(value.points) &&
    value.points >= 0
      ? value.points
      : undefined

  const shared = {
    id,
    question,
    ...(explanation ? {explanation} : {}),
    ...(points !== undefined ? {points} : {}),
  }

  if (type === 'true_false') {
    const answer =
      value.answer === 'true' || value.answer === 'false'
        ? value.answer
        : undefined
    if (!answer) return undefined
    return {type, answer, ...shared}
  }

  // The two choice types: alternatives, at least two of them, and a right answer.
  const options = usableOptions(value.options, MATERIAL_QUESTION_MAX_OPTIONS)
  const correct = options.filter(option => option.isCorrect)
  if (options.length < 2 || correct.length === 0) return undefined
  if (type === 'single_choice' && correct.length > 1) {
    // Keep the first; the rest are alternatives the author can re-mark by editing.
    let seen = false
    for (const option of options) {
      const isFirst = option.isCorrect && !seen
      if (option.isCorrect) seen = true
      option.isCorrect = isFirst
    }
  }
  return {type, options, ...shared}
}

/**
 * A page's material, normalised — or `undefined` when the page carries nothing.
 *
 * `pageCount` is optional because the composer authors material against a file it
 * has already counted, while the read path is often handed a record whose page
 * count it does not know yet; a page beyond the book's own length is dropped when
 * the count *is* known, because questions on a page that does not exist are
 * questions nobody will ever see.
 */
export function normalizePageMaterial(
  value: unknown,
  {pageCount, did}: {pageCount?: number; did?: string} = {},
): DocumentPageMaterial | undefined {
  if (!isObject(value)) return undefined
  const page = pageNumber(value.page)
  if (page === undefined) return undefined
  if (pageCount !== undefined && page > pageCount) return undefined

  const audio = blobRefOf(value.audio)
  /*
   * The address is derived from the reference when the record does not name one.
   * `did` is the author's, and a row cannot be fetched without it, so a caller
   * that has no DID gets no derived address — and the row still normalises, so a
   * record read back for its *counts* does not depend on who is asking.
   */
  const audioUrl =
    text(value.audioUrl, 2048) ??
    (audio && did ? buildPdsBlobUrl(did, audio.ref.$link) : undefined)
  const questions: Slide[] = []
  const seenIds = new Set<string>()
  if (Array.isArray(value.questions)) {
    for (const raw of value.questions) {
      const question = normalizeQuestion(raw)
      if (!question) continue
      // Two questions with one id would share a result row and a report line.
      if (seenIds.has(question.id)) continue
      seenIds.add(question.id)
      questions.push(question)
      if (questions.length >= DOCUMENT_PAGE_MAX_QUESTIONS) break
    }
  }

  if (!audioUrl && !audio && questions.length === 0) return undefined
  // One clip per page is expressed by the *clip* being one URL and one blob
  // rather than a list, so there is nothing to cap here — a record cannot carry
  // two, whichever of the two a writer filled in.
  return {
    page,
    ...(audioUrl ? {audioUrl} : {}),
    ...(audio ? {audio} : {}),
    questions,
  }
}

/**
 * A well-formed blob reference, or `undefined`.
 *
 * A reference with no CID names nothing — the bytes could not be found and no
 * address could be derived from it — so it is dropped rather than kept as an
 * empty promise of a clip.
 */
function blobRefOf(value: unknown): DocumentBlobRef | undefined {
  if (!isObject(value)) return undefined
  const ref = value.ref
  const link = isObject(ref) ? text(ref.$link, 200) : undefined
  if (!link) return undefined
  return {
    $type: 'blob',
    ref: {$link: link},
    mimeType: text(value.mimeType, 100) ?? 'audio/mpeg',
    size: typeof value.size === 'number' && value.size >= 0 ? value.size : 0,
  }
}

/**
 * A whole document's material, normalised: pages ordered, duplicates collapsed,
 * empty rows gone — or `undefined` when nothing is left, so callers never have to
 * test `pages.length` to know whether there is material.
 */
export function normalizeDocumentMaterial(
  value: unknown,
  {pageCount, did}: {pageCount?: number; did?: string} = {},
): DocumentMaterial | undefined {
  if (!isObject(value)) return undefined
  if (!Array.isArray(value.pages)) return undefined

  const byPage = new Map<number, DocumentPageMaterial>()
  for (const raw of value.pages) {
    const page = normalizePageMaterial(raw, {pageCount, did})
    if (!page) continue
    const existing = byPage.get(page.page)
    if (!existing) {
      byPage.set(page.page, page)
      continue
    }
    /*
     * Two rows for one page: the later one wins for the audio (there is only one
     * clip) and the questions are *merged*, in order, up to the cap. Dropping the
     * earlier row's questions would lose work an author really did, and keeping
     * the earlier audio when the later row names one would keep the stale clip.
     */
    const questions = [...existing.questions]
    for (const question of page.questions) {
      if (questions.length >= DOCUMENT_PAGE_MAX_QUESTIONS) break
      if (questions.some(q => q.id === question.id)) continue
      questions.push(question)
    }
    byPage.set(page.page, {
      page: page.page,
      ...(page.audioUrl ? {audioUrl: page.audioUrl} : {}),
      ...(page.audio ? {audio: page.audio} : {}),
      questions,
    })
  }

  if (byPage.size === 0) return undefined
  return {pages: [...byPage.values()].sort((a, b) => a.page - b.page)}
}

/**
 * The companion record, built from the material.
 *
 * The pages are normalised on the way in, exactly as the read path normalises
 * them on the way out, so a record this writer produces is a record this reader
 * would have produced: the caps are applied once, at the only moment an author
 * can exceed them, and nothing downstream has to defend against a row that was
 * never valid. An empty material still yields a record — the caller decides
 * whether there is anything worth writing, and `pages: []` is a truthful answer
 * to "what does this document hold".
 */
export function buildDocumentMaterialRecord({
  material,
  documentUri,
  createdAt,
  pageCount,
}: {
  material: DocumentMaterial
  /** The post the material belongs to, by URI. */
  documentUri: string
  createdAt: string
  /** The book's length, when it is known, so pages past the end are dropped. */
  pageCount?: number
}): DocumentMaterialRecord {
  const normalized = normalizeDocumentMaterial(material, {pageCount})
  return {
    $type: DOCUMENT_MATERIAL_RECORD_TYPE,
    document: {uri: documentUri},
    pages: normalized?.pages ?? [],
    createdAt,
  }
}

/** The page's row, or `undefined` — the single lookup every surface uses. */
export function pageMaterialFor(
  material: DocumentMaterial | undefined,
  page: number,
): DocumentPageMaterial | undefined {
  return material?.pages.find(row => row.page === page)
}

/**
 * Whether a page carries a clip — either half of one counts.
 *
 * Asked of both, because a row can name its clip by address, by blob, or by
 * both, and all three are a page with audio on it. A page that named only its
 * blob and was counted as silent would be a badge that hid the very clip the
 * reader can play.
 */
export function hasPageAudio(page: DocumentPageMaterial | undefined): boolean {
  return !!(page?.audioUrl || page?.audio)
}

/** One page's counts, so a badge and the sheet's counter cannot disagree. */
export function pageCountsOf(
  material: DocumentMaterial | undefined,
  page: number,
): MaterialPageCounts {
  const row = pageMaterialFor(material, page)
  return {
    audio: hasPageAudio(row) ? 1 : 0,
    questions: row?.questions.length ?? 0,
  }
}

/**
 * Whether a question row holds anything its author typed.
 *
 * Asked before a row is called a *draft*. The editor mints a row the moment a type
 * is pressed in the picker, so a row nobody has written in is a row with nothing
 * to lose — and a page that stopped its author to ask about one would be a page
 * that taught them to dismiss the stop without reading it.
 *
 * Asked by comparing the row against the row its type *starts* as — the one the
 * picker mints (`createSlide`). Anything else is content, and that is deliberately
 * whatever the row carries rather than "only what would publish": a prompt with no
 * correct answer yet is not a question the page could keep, and it is exactly the
 * half-written thing an author would lose by turning the page.
 *
 * Comparing against the blank is what keeps the two defaults a row is born with
 * from reading as content: the id it was minted with (dropped, like every other
 * id) and `true_false`'s seeded `answer` of `"true"`, which would otherwise make
 * every row an author merely opened a draft.
 */
export function questionRowHasContent(row: Slide): boolean {
  return (
    stableKey(withoutRowIds(row)) !==
    stableKey(withoutRowIds(createSlide(row.type)))
  )
}

/**
 * A page's draft, in a form two of them can be compared by.
 *
 * The editor is the only place a page's draft exists — `Confirm` is what writes it
 * to the post — so everything that would close the card has to be able to ask
 * whether it would be throwing something away. That question is a comparison of
 * two drafts, and this is the comparison: what the card holds now, against what
 * the page already carries.
 *
 * Rows that were opened and left blank are dropped, for the same reason they are
 * not called drafts above: an author who pressed a type and changed their mind has
 * written nothing, and a comparison that counted the empty row would call that
 * nothing a change to protect.
 *
 * Ids are dropped too — from the rows and from everything on them. Every row is
 * minted a fresh id by the studio's editors, and the same content comes back out
 * of the record wearing the id it was saved with, so two drafts that differ only
 * by identity are the same draft. What is left is compared with its keys in a
 * fixed order, since the row an author is writing and the row that comes back off
 * the record are built by different code and do not agree on which field was set
 * first.
 */
export function pageMaterialDraftKey(draft: {
  audioUrl?: string
  questions: readonly Slide[]
}): string {
  return stableKey([
    draft.audioUrl ?? null,
    draft.questions.filter(questionRowHasContent).map(withoutRowIds),
  ])
}

/** A value with every `id` removed, at every depth. */
function withoutRowIds(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(withoutRowIds)
  if (isObject(value)) {
    const out: Record<string, unknown> = {}
    for (const [key, entry] of Object.entries(value)) {
      if (key !== 'id') out[key] = withoutRowIds(entry)
    }
    return out
  }
  return value
}

/** A value as text, with its object keys sorted, so two shapes compare by content. */
function stableKey(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableKey).join(',')}]`
  }
  if (isObject(value)) {
    const body = Object.keys(value)
      .sort()
      .map(key => `${JSON.stringify(key)}:${stableKey(value[key])}`)
      .join(',')
    return `{${body}}`
  }
  return JSON.stringify(value) ?? 'null'
}

/** The deck's totals, summed from the rows rather than stored beside them. */
export function countsOf(
  material: DocumentMaterial | undefined,
): MaterialTotals {
  let audio = 0
  let questions = 0
  for (const row of material?.pages ?? []) {
    if (hasPageAudio(row)) audio += 1
    questions += row.questions.length
  }
  return {audio, questions, pagesWithMaterial: material?.pages.length ?? 0}
}

/**
 * Replaces one page's material, or clears it when `pageMaterial` is `undefined`.
 *
 * This is the composer's write path, and it keeps the invariant the rest of this
 * file assumes: rows exist only for pages that carry something, and they stay
 * ordered. Clearing the last row clears the material itself, which is what makes
 * "remove the questions and the post no longer mentions any" true.
 */
export function withPageMaterial(
  material: DocumentMaterial | undefined,
  page: number,
  pageMaterial: DocumentPageMaterial | undefined,
): DocumentMaterial | undefined {
  const rows = (material?.pages ?? []).filter(row => row.page !== page)
  // The row is normalised, not trusted: a caller that emptied a page's questions
  // and kept its audio URL has not cleared the page, and one that emptied both by
  // handing over a row with nothing in it *has* — without the caller having to
  // know the difference.
  const row = pageMaterial ? normalizePageMaterial(pageMaterial) : undefined
  if (row) rows.push(row)
  if (rows.length === 0) return undefined
  return {pages: rows.slice().sort((a, b) => a.page - b.page)}
}

/** The page table to denormalize onto the post, built from the material itself. */
export function materialPagesOf(
  material: DocumentMaterial | undefined,
): {page: number; audio: number; questions: number}[] {
  return (material?.pages ?? []).map(row => ({
    page: row.page,
    audio: hasPageAudio(row) ? 1 : 0,
    questions: row.questions.length,
  }))
}

/** The reference a post carries: where the material is, and what it holds. */
export function materialRefOf(
  material: DocumentMaterial | undefined,
  uri: string,
): DocumentMaterialRef | undefined {
  const pages = materialPagesOf(material)
  if (!uri || pages.length === 0) return undefined
  return {uri, pages}
}

/**
 * The denormalized table, read back off a record or a view — or `undefined`.
 *
 * Every number is re-established rather than trusted: a negative or fractional
 * count, a page zero, a row that says it holds nothing, a duplicate page and an
 * out-of-range page number are all dropped, because this array is what draws the
 * badges and a card that says "−1 questions" is worse than one that says nothing.
 * The counts are clamped to the caps for the same reason.
 */
export function normalizeMaterialPages(
  value: unknown,
): {page: number; audio: number; questions: number}[] {
  if (!Array.isArray(value)) return []
  const byPage = new Map<
    number,
    {page: number; audio: number; questions: number}
  >()
  for (const raw of value) {
    if (!isObject(raw)) continue
    const page = pageNumber(raw.page)
    if (page === undefined) continue
    const audio = clampCount(raw.audio, DOCUMENT_PAGE_MAX_AUDIO)
    const questions = clampCount(raw.questions, DOCUMENT_PAGE_MAX_QUESTIONS)
    if (audio === 0 && questions === 0) continue
    byPage.set(page, {page, audio, questions})
  }
  return [...byPage.values()].sort((a, b) => a.page - b.page)
}

function clampCount(value: unknown, max: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0
  const whole = Math.floor(value)
  if (whole <= 0) return 0
  return Math.min(whole, max)
}

function materialRefFromEmbed(embed: unknown): DocumentMaterialRef | undefined {
  if (!isObject(embed)) return undefined
  const uri = embed[MATERIAL_EMBED_URI_KEY]
  const pages = normalizeMaterialPages(embed[MATERIAL_EMBED_PAGES_KEY])
  // No address means no material, however many counts a record claims: the counts
  // describe what is *at* that address, and without it there is nothing to play.
  if (typeof uri !== 'string' || !uri || pages.length === 0) return undefined
  return {uri, pages}
}

/**
 * The material a post declares, read from the post's own record.
 *
 * The served embed view is built from a fixed set of fields, so the material
 * reference lives only on the record — and a post view carries its record. Every
 * surface that draws material badges is handed a post, so this is the one reader
 * they share; it accepts a `recordWithMedia` embed too, because a document quoted
 * into a post is still a document.
 */
export function materialRefFromRecord(
  record: unknown,
): DocumentMaterialRef | undefined {
  if (!isObject(record)) return undefined
  return materialRefFromEmbed(documentMediaOf(record.embed))
}

/** The same reference off a served view, once the app view mirrors the fields. */
export function materialRefFromView(
  view: unknown,
): DocumentMaterialRef | undefined {
  if (!isObject(view)) return undefined
  return materialRefFromEmbed(documentMediaOf(view.embed))
}

/** A post embed's media, when the post is a plain document or a quote + document. */
function documentMediaOf(embed: unknown): unknown {
  if (!isObject(embed)) return undefined
  if (embed.$type === 'app.bsky.embed.recordWithMedia') return embed.media
  return embed
}

/** The total a badge shows, summed from the denormalized page table. */
export function totalsOf(ref: DocumentMaterialRef | undefined): MaterialTotals {
  let audio = 0
  let questions = 0
  for (const row of ref?.pages ?? []) {
    audio += row.audio
    questions += row.questions
  }
  return {audio, questions, pagesWithMaterial: ref?.pages.length ?? 0}
}

/**
 * What has become of a document's material, as far as anybody knows.
 *
 * The distinction this type exists for is `asking` against `none`, because from
 * inside the reader those two look identical: no material, nothing to play. One
 * is worth waiting for (the questions are on their way) and the other is not —
 * the record answered, and its answer was nothing, so a page that says it asks
 * something can never be asked it. Collapsing the two is how a spinner ends up on
 * screen with nothing behind it.
 *
 * `failed` is the third of those, and it is the same mistake one step further
 * out: a read that did not get through is *also* not an answer (`asking` and
 * `none` are the two states a reader can be told apart, and a failure is neither
 * — "nothing there" is a fact about the document, and this is a fact about the
 * wire). A record that could not be read is worth asking for again, by hand,
 * because everything it holds is still there: it has been *written* once, with
 * the post, and never edited. Anything that treats it as `none` — the reader
 * dropping the card, the card drawing no questions, a badge that stops offering
 * — turns a bad minute into a document whose questions are gone for the session.
 */
export type DocumentMaterialState =
  /** Nobody has asked: no request has been made, and none is expected yet. */
  | 'unasked'
  /** Asked, and the answer has not arrived. */
  | 'asking'
  /** In hand. */
  | 'ready'
  /** Asked, answered, and there is nothing there. */
  | 'none'
  /** Asked, and the reading itself did not get through. Not an answer, and not "nothing there". */
  | 'failed'

/**
 * The state a material query is in, from the four things that decide it.
 *
 * `data` is half the question: the hook that reads a document's material answers
 * `null` for "there is nothing there" and leaves `undefined` for "nobody has
 * looked", and only with both in hand can a reader tell waiting from empty.
 *
 * `fetchStatus` is the other half, and it is taken as the hook reports it rather
 * than as a boolean, because the two ways a fetch can be unfinished are one thing
 * to a reader: a request on the wire, and one the app is holding back until it
 * believes it is online. Both are questions *still coming*, and treating the
 * second as "nothing there" is how a reader is walked past questions that were
 * never asked for. Only `idle` — nothing in flight, nothing coming — makes an
 * absent record an answer.
 *
 * `isError` is what that answer *is*, once nothing is in flight: the query
 * throws every refusal that is not "the record is not there" (`isRecordNotFound`
 * in `#/state/queries/document-material`), so an errored query with no data is a
 * read that failed rather than a document with nothing in it. It is asked after
 * `fetchStatus` on purpose — a retry that is in the air is a question *coming*,
 * and a card that said "we could not read this" over a request it had just sent
 * would be describing the last attempt while making a new one.
 */
export function documentMaterialState({
  asked,
  data,
  fetchStatus,
  isError,
}: {
  /** Whether anything has asked for the record yet. */
  asked: boolean
  /** The material, `null` for "nothing there", `undefined` for untried. */
  data: DocumentMaterial | null | undefined
  /** What the fetch itself is doing. */
  fetchStatus: 'fetching' | 'paused' | 'idle'
  /** Whether the last attempt ended in a refusal that is not an answer. */
  isError: boolean
}): DocumentMaterialState {
  if (data) return 'ready'
  if (!asked) return 'unasked'
  if (fetchStatus !== 'idle') return 'asking'
  return isError ? 'failed' : 'none'
}

/** One page's counts from the denormalized table — no fetch, no lookup. */
export function materialPageCounts(
  ref: DocumentMaterialRef | undefined,
  page: number,
): MaterialPageCounts {
  const row = ref?.pages.find(entry => entry.page === page)
  return {audio: row?.audio ?? 0, questions: row?.questions ?? 0}
}

/**
 * The first page that asks something — where playing a document starts.
 *
 * A reader who presses play on the *document* is asking for its questions, not for
 * its first page: a deck whose questions begin on page 4 would otherwise open on a
 * page that asks nothing and stay silent until the reader turned one. This is the
 * page such a press opens on, so the first thing it shows is a question.
 *
 * `undefined` when no page asks anything, which is the only case where there is
 * nothing to start — and the case that keeps the caller from offering a play it
 * could not begin.
 */
export function firstQuestionPage(
  ref: DocumentMaterialRef | undefined,
): number | undefined {
  let first: number | undefined
  for (const row of ref?.pages ?? []) {
    if (row.questions > 0 && (first === undefined || row.page < first)) {
      first = row.page
    }
  }
  return first
}

/**
 * Material trimmed to fit the record budget, with the pages it dropped.
 *
 * The caps bound one page; this bounds the whole deck, and it gives up its last
 * pages rather than its questions, because a book that is half-material is worth
 * more than one whose record will not write. Pages are removed whole so a page is
 * never *partly* described — an author who has to trim is better served by a
 * clean edge than by a page whose fifth question vanished.
 *
 * The caller is told how many pages went, so the publish path can say so instead
 * of quietly publishing less than it was shown.
 */
export function trimMaterialToBudget(
  material: DocumentMaterial,
  {
    budgetBytes = MATERIAL_MAX_JSON_BYTES,
    sizeOf = (pages: DocumentPageMaterial[]) => JSON.stringify(pages).length,
  }: {
    /** The JSON budget the record has to fit inside. */
    budgetBytes?: number
    /** How the caller measures a candidate, so this stays free of record shape. */
    sizeOf?: (pages: DocumentPageMaterial[]) => number
  } = {},
): {material: DocumentMaterial; droppedPages: number[]} {
  const pages = material.pages.slice()
  const droppedPages: number[] = []
  while (pages.length > 0 && sizeOf(pages) > budgetBytes) {
    const removed = pages.pop()
    if (removed) droppedPages.unshift(removed.page)
  }
  return {material: {pages}, droppedPages}
}
